// Optional Sentry imports - gracefully handle if not available
let Sentry: any = null;
let BrowserTracing: any = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  Sentry = require('@sentry/react');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  BrowserTracing = require('@sentry/tracing').BrowserTracing;
} catch (e) {
  // Sentry not available - will use no-op functions
  console.warn('Sentry packages not available. Error tracking disabled.');
}

/**
 * Initialize Sentry error tracking
 * Call this once when the app starts
 */
export function initErrorTracking() {
  if (!Sentry || !BrowserTracing) {
    console.warn('Sentry not available. Error tracking disabled.');
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      new BrowserTracing({
        // Set tracing origins to include your app
        tracingOrigins: ['localhost', /^\//],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0, // 10% in production, 100% in dev
    // Session Replay (optional, can be expensive)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    environment: process.env.NODE_ENV || 'development',
    
    // Filter out known non-critical errors
    beforeSend(event: any, hint: any) {
      // Allow testing in development if REACT_APP_SENTRY_TEST_MODE is enabled
      const testMode = process.env.REACT_APP_SENTRY_TEST_MODE === 'true';
      
      // Don't send errors in development (unless test mode is enabled)
      if (process.env.NODE_ENV !== 'production' && !testMode) {
        return null;
      }
      
      // Filter out browser extension errors
      if (event.exception) {
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message);
          if (
            message.includes('chrome-extension://') ||
            message.includes('moz-extension://') ||
            message.includes('safari-extension://')
          ) {
            return null;
          }
        }
      }
      
      return event;
    },
  });
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: { id: string; email?: string }) {
  if (!Sentry) return;
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  if (!Sentry) return;
  Sentry.setUser(null);
}

/**
 * Log an error to Sentry
 */
export function logError(error: Error, context?: Record<string, any>) {
  if (!Sentry) return;
  Sentry.captureException(error, {
    contexts: {
      custom: context || {},
    },
  });
}

/**
 * Log a message to Sentry
 */
export function logMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (!Sentry) return;
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category?: string, data?: Record<string, any>) {
  if (!Sentry) return;
  Sentry.addBreadcrumb({
    message,
    category: category || 'default',
    data,
    level: 'info',
  });
}


